import { z } from "zod";
import type { WorkObjectTypeDefinition } from "../../../../entities/work-object-type/model/use-work-object-types";
import { slugifyWorkObjectTypeKey } from "../../../../entities/work-object-type/model/work-object-type-key";

export type WorkObjectTypeFormValues = {
  categoryId: string | null;
  icon: string;
  key: string;
  name: string;
};

export type WorkObjectTypeFormErrors = Partial<Record<keyof WorkObjectTypeFormValues, string>>;

export type WorkObjectTypeFormValidation =
  | {
      data: z.infer<typeof workObjectTypeFormSchema>;
      errors: WorkObjectTypeFormErrors;
      success: true;
    }
  | {
      data: null;
      errors: WorkObjectTypeFormErrors;
      success: false;
    };

const workObjectTypeFormSchema = z.object({
  categoryId: z.string().nullable(),
  icon: z.string().trim().min(1, { error: "Choose an icon." }).max(80, { error: "Icon name is too long." }),
  key: z
    .string()
    .transform((value) => slugifyWorkObjectTypeKey(value))
    .pipe(
      z
        .string()
        .min(2, { error: "Use at least 2 characters." })
        .max(48, { error: "Use 48 characters or fewer." }),
    ),
  name: z
    .string()
    .trim()
    .min(2, { error: "Use at least 2 characters." })
    .max(80, { error: "Use 80 characters or fewer." }),
});

export function validateWorkObjectTypeForm(
  values: WorkObjectTypeFormValues,
  existingTypes: WorkObjectTypeDefinition[],
  currentTypeId: string | null,
): WorkObjectTypeFormValidation {
  const result = workObjectTypeFormSchema.safeParse(values);
  if (!result.success) {
    return {
      data: null,
      errors: firstFieldErrors(result.error.flatten().fieldErrors),
      success: false,
    };
  }

  const keyExists = existingTypes.some((type) => type.id !== currentTypeId && type.key === result.data.key);
  if (keyExists) {
    return {
      data: null,
      errors: { key: "This key is already used." },
      success: false,
    };
  }

  return {
    data: result.data,
    errors: {},
    success: true,
  };
}

function firstFieldErrors(errors: z.inferFlattenedErrors<typeof workObjectTypeFormSchema>["fieldErrors"]) {
  return Object.fromEntries(
    Object.entries(errors)
      .filter(([, messages]) => messages?.[0])
      .map(([field, messages]) => [field, messages?.[0]]),
  ) as WorkObjectTypeFormErrors;
}
